import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { Button, IconEditAlt, IconRefresh } from '@infologistics/frontend-libraries'
import documentsService from '@services/documents'
import { IApplicationState } from '@store/types/commonTypes'
import { actions, getDocumentData } from '@store/modules/documents/actions'
import { AllowedAction } from '@const/consts'
import { DocumentMode } from '@store/modules/document/types'
import { displayBasicNotification, displayConfirmNotification } from '@utils/utils'
import { CoeditEditor, parseEditor } from './editorUrl'
import styles from './Coedit.module.css'
import fileStyles from '../File/File.module.css'

type Pending = 'start' | 'finish' | 'sync' | null

const Coedit: FC<{ userOguid: string, orgOguid: string, documentOguid: string }> = ({ userOguid, orgOguid, documentOguid }) => {
  const { t } = useTranslation('document')
  const dispatch = useDispatch<any>()
  const store = useStore<IApplicationState>()
  const currentDocument = useSelector((state: IApplicationState) => state.documents.currentDocument)
  const { coeditState, coeditOperationOguid, coeditUrl, allowedActions, canFinishCoedit } = currentDocument
  const mode = useSelector((state: IApplicationState) => state.document.mode)
  const canStart = mode === DocumentMode.VIEW && allowedActions.includes(AllowedAction.COEDIT)
  const [pending, setPending] = useState<Pending>(null)
  const [editor, setEditor] = useState<CoeditEditor | null>(null)
  const [retryStart, setRetryStart] = useState(false)
  const [syncFailed, setSyncFailed] = useState(false)
  const pathname = useRef(window.location.pathname)
  const mounted = useRef(false)
  const busy = useRef<Pending>(null)
  const reading = useRef<Promise<void> | null>(null)
  const confirming = useRef(false)

  const isCurrent = useCallback(() => {
    const state = store.getState()
    return mounted.current && window.location.pathname === pathname.current && state.user.profile.oguid === userOguid && state.user.activeOrganization.oguid === orgOguid &&
      state.documents.currentDocument.oguid === documentOguid && state.document.mode === DocumentMode.VIEW && !state.loader.isLoading
  }, [store, userOguid, orgOguid, documentOguid])

  const notifyError = useCallback((error: AxiosError<{ message?: string }>) => {
    if (!isCurrent() || error.config?.signal?.aborted) return
    displayBasicNotification({ title: '', type: 'error', content: error.response?.data?.message || t('document:coedit.requestFailed') })
  }, [isCurrent, t])

  const synchronize = useCallback((supersedePendingReads = false): Promise<void> => {
    if (reading.current) return reading.current
    if (!isCurrent()) return Promise.resolve()
    const request: Promise<void> = (async () => {
      const state = store.getState().documents.currentDocument.coeditState
      let isFileUpdated = false
      try {
        if (!busy.current && (state === 'STARTING' || state === 'ACTIVE')) {
          const { data } = await documentsService.getCoeditStatus(orgOguid, documentOguid)
          isFileUpdated = data.isFileUpdated
        }
      } finally {
        if (isCurrent()) {
          await dispatch(getDocumentData(documentOguid, false, isCurrent, supersedePendingReads))
          if (isCurrent() && (isFileUpdated || (!busy.current && state === 'ACTIVE' && store.getState().documents.currentDocument.coeditState === 'READY'))) {
            dispatch(actions.clearCurrentDocumentFiles())
            dispatch(actions.setReloadCurrentDocumentFile(true))
          }
        }
      }
      if (isCurrent()) setSyncFailed(false)
    })()
      .catch((error: AxiosError<{ message?: string }>) => {
        if (isCurrent()) setSyncFailed(true)
        throw error
      })
      .finally(() => { if (reading.current === request) reading.current = null })
    reading.current = request
    return request
  }, [dispatch, store, orgOguid, documentOguid, isCurrent])

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => { setSyncFailed(false) }, [currentDocument])

  // Reconcile the editor with a new card, not with completion of a local request.
  useEffect(() => {
    const active = coeditState === 'STARTING' || coeditState === 'ACTIVE'
    if (!active) {
      if (busy.current !== 'start') setEditor(null)
      setRetryStart(false)
    } else if (busy.current !== 'start') {
      setEditor(coeditOperationOguid ? parseEditor(coeditUrl, coeditOperationOguid) : null)
    }
  }, [currentDocument])

  const run = async (kind: Exclude<Pending, null>, action: () => Promise<void>) => {
    if (busy.current || !isCurrent()) return
    busy.current = kind
    setPending(kind)
    try {
      // Finish any older read before issuing a mutation and reading its result.
      await reading.current?.catch(() => undefined)
      if (isCurrent()) await action()
    } catch (error) {
      notifyError(error as AxiosError<{ message?: string }>)
    } finally {
      busy.current = null
      if (mounted.current) setPending(null)
    }
  }

  const start = () => run('start', async () => {
    const current = store.getState().documents.currentDocument
    if (!current.allowedActions.includes(AllowedAction.COEDIT) ||
      (current.coeditState !== 'READY' && !(retryStart && current.coeditState === 'STARTING'))) return
    let startFailed = false
    let editorUrl: string | null = null
    try {
      const { data } = await documentsService.startCoedit(orgOguid, documentOguid)
      if (!isCurrent()) return
      const url = new URL(data.coeditUrl)
      if (!data.coeditSessionOguid || !['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid editor response')
      editorUrl = url.href
      setPending('sync')
      setRetryStart(false)
      setEditor({ url: url.href, operation: null })
      window.open(url.href, '_blank', 'noopener,noreferrer')
    } catch (error) {
      startFailed = true
      if (isCurrent()) {
        const status = (error as AxiosError).response?.status
        setRetryStart(!status || status >= 500)
      }
      throw error
    } finally {
      if (isCurrent()) await synchronize(true).catch(error => {
        if (!startFailed) notifyError(error)
      })
    }
    if (isCurrent() && editorUrl) {
      const { coeditState: state, coeditOperationOguid: operation } = store.getState().documents.currentDocument
      if (operation && (state === 'STARTING' || state === 'ACTIVE')) {
        setEditor(parseEditor(store.getState().documents.currentDocument.coeditUrl, operation))
      }
    }
  })

  const reloadFile = () => {
    if (!isCurrent()) return
    dispatch(actions.clearCurrentDocumentFiles())
    dispatch(actions.setReloadCurrentDocumentFile(true))
  }

  const finish = () => {
    const operation = coeditOperationOguid
    if (!operation || !canFinishCoedit || busy.current || confirming.current) return
    confirming.current = true
    displayConfirmNotification({
      title: t('document:coedit.finish'),
      content: t('document:coedit.confirmFinish'),
      submitText: t('document:coedit.finish'),
      cancelText: t('common:cancel'),
      cancelOnClose: true,
      onCancel: () => { confirming.current = false },
      onSubmit: () => {
        confirming.current = false
        return run('finish', async () => {
          const current = store.getState().documents.currentDocument
          if (!current.allowedActions.includes(AllowedAction.COEDIT) ||
            current.canFinishCoedit !== true || current.coeditOperationOguid !== operation) return
          let finished = false
          try {
            await documentsService.finishCoedit(orgOguid, documentOguid, { coeditOperationOguid: operation })
            finished = true
            if (isCurrent()) {
              setEditor(null)
              const document = store.getState().documents.currentDocument
              if (document.coeditOperationOguid === operation) {
                // The toolbar remounts on PDF reload, including when the card read fails.
                dispatch(actions.setCurrentDocument({ ...document, coeditUrl: null }))
              }
            }
            await synchronize(true)
          } catch (error) {
            await synchronize(true).catch(() => undefined)
            throw error
          } finally {
            if (finished) reloadFile()
          }
        })
      }
    })
  }

  if (!canStart || !['READY', 'STARTING', 'ACTIVE'].includes(coeditState)) return null
  const active = coeditState === 'ACTIVE'
  const disabled = !!pending
  const starting = pending === 'start' || (coeditState === 'STARTING' && !editor && !retryStart)

  const buttonClasses = `${fileStyles.button_link} d-flex align-items-center justify-content-center p-0`
  const iconProps = { externalClass: fileStyles.button_icon }

  return (
    <div className={styles.controls} aria-busy={starting || !!pending}>
      {active
        ? canFinishCoedit === true && <Button theme='text' classes={`${buttonClasses} ${styles.stop}`} title={t('document:coedit.finish')} isDisabled={disabled || !coeditOperationOguid} onClick={finish}>
          <span className={pending === 'finish' ? styles.spinner : styles.stop_icon} aria-hidden='true' /><span className={styles.srOnly}>{t('document:coedit.finish')}</span>
        </Button>
        : <Button theme='text' classes={buttonClasses} title={t('document:coedit.start')} isDisabled={coeditState !== 'READY' || disabled || syncFailed} onClick={start}>
          {starting ? <span className={styles.spinner} aria-hidden='true' /> : <IconEditAlt {...iconProps} />}<span className={styles.srOnly}>{t('document:coedit.start')}</span>
        </Button>}
      {editor && <a className={buttonClasses} title={t('document:coedit.open')} href={editor.url} target='_blank' rel='noopener noreferrer'>
        <IconEditAlt {...iconProps} /><span className={styles.srOnly}>{t('document:coedit.open')}</span>
      </a>}
      {retryStart && coeditState === 'STARTING' && <Button theme='text' classes={buttonClasses} title={t('document:coedit.retryStart')} isDisabled={disabled} onClick={start}>
        <IconRefresh {...iconProps} /><span className={styles.srOnly}>{t('document:coedit.retryStart')}</span>
      </Button>}
    </div>
  )
}

export default Coedit
